import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import * as d3 from 'd3';
import { useCalcAmountDisplayMode } from './calc_components/useCalcAmountDisplayMode';
import './EmiSplitChart.css';

const MARGIN = { top: 28, right: 14, bottom: 44, left: 54 };

const formatterINR = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatAxisInr(value) {
  const n = Number(value) || 0;
  const lakhs = n / 100000;
  if (Math.abs(lakhs) >= 100) return `₹${d3.format(',.0f')(lakhs)}L`;
  if (Math.abs(lakhs) >= 10) return `₹${d3.format('.1f')(lakhs)}L`;
  if (Math.abs(lakhs) >= 1) return `₹${d3.format('.2f')(lakhs)}L`;
  return formatterINR.format(Math.round(n));
}

function useChartSize(ref) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setSize({
        width: Math.max(0, Math.floor(rect.width)),
        height: Math.max(0, Math.floor(rect.height)),
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);

  return size;
}

function barYear(d) {
  return d?.data?.year;
}

/**
 * Hover wins: only that year is lit.
 * Otherwise years <= throughYear are lit; later years are dimmed.
 */
function applyBarHighlight(svgEl, throughYear, hoverYear) {
  if (!svgEl) return;
  const svg = d3.select(svgEl);
  svg.selectAll('.emi-split-chart__bar').each(function apply(d) {
    const yr = barYear(d);
    let lit;
    if (hoverYear != null && Number.isFinite(hoverYear)) {
      lit = yr === hoverYear;
    } else {
      lit = yr <= throughYear;
    }
    d3.select(this)
      .classed('emi-split-chart__bar--lit', lit)
      .classed('emi-split-chart__bar--dim', !lit);
  });
}

function drawEmiSplitChart(
  svgEl,
  tipEl,
  yearRows,
  width,
  height,
  throughYear,
  onHoverYear,
  formatMoney,
) {
  d3.select(svgEl).selectAll('*').remove();
  if (tipEl) {
    tipEl.style.opacity = '0';
    tipEl.innerHTML = '';
  }

  const rows = Array.isArray(yearRows) ? yearRows : [];
  if (!rows.length || width < 80 || height < 80) return;

  const money =
    typeof formatMoney === 'function'
      ? formatMoney
      : (v) => formatterINR.format(Math.round(v));

  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;
  const svg = d3.select(svgEl).attr('viewBox', `0 0 ${width} ${height}`);
  const g = svg
    .append('g')
    .attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

  const x = d3
    .scaleBand()
    .domain(rows.map((d) => d.year))
    .range([0, innerW])
    .paddingInner(0.22)
    .paddingOuter(0.06);

  const yMax = d3.max(rows, (d) => d.annualTotal) || 0;
  const y = d3
    .scaleLinear()
    .domain([0, yMax * 1.08])
    .nice()
    .range([innerH, 0]);

  const stack = d3
    .stack()
    .keys(['interest', 'principal'])
    .order(d3.stackOrderNone)
    .offset(d3.stackOffsetNone);

  const series = stack(rows);

  g.append('g')
    .attr('class', 'emi-split-chart__axis emi-split-chart__axis--x')
    .attr('transform', `translate(0,${innerH})`)
    .call(
      d3
        .axisBottom(x)
        .tickValues(
          rows
            .map((d) => d.year)
            .filter((yr, _i, arr) => {
              if (arr.length <= 12) return true;
              if (yr === 1 || yr === arr[arr.length - 1]) return true;
              const step = Math.ceil(arr.length / 8);
              return (yr - 1) % step === 0;
            }),
        )
        .tickFormat((d) => `Y${d}`),
    );

  g.append('g')
    .attr('class', 'emi-split-chart__axis emi-split-chart__axis--y')
    .call(d3.axisLeft(y).ticks(5).tickFormat(formatAxisInr));

  g.append('text')
    .attr('class', 'emi-split-chart__axis-label')
    .attr('x', innerW / 2)
    .attr('y', innerH + 36)
    .attr('text-anchor', 'middle')
    .text('Loan year');

  const layers = g
    .selectAll('.emi-split-chart__layer')
    .data(series)
    .enter()
    .append('g')
    .attr('class', (d) => `emi-split-chart__layer emi-split-chart__layer--${d.key}`);

  layers
    .selectAll('rect')
    .data((d) => d)
    .enter()
    .append('rect')
    .attr('class', 'emi-split-chart__bar emi-split-chart__bar--lit')
    .attr('x', (d) => x(d.data.year))
    .attr('y', (d) => y(d[1]))
    .attr('width', x.bandwidth())
    .attr('height', (d) => Math.max(0, y(d[0]) - y(d[1])))
    .attr('rx', Math.min(3, x.bandwidth() / 4))
    .attr('ry', Math.min(3, x.bandwidth() / 4));

  const hit = g
    .selectAll('.emi-split-chart__hit')
    .data(rows)
    .enter()
    .append('rect')
    .attr('class', 'emi-split-chart__hit')
    .attr('x', (d) => x(d.year))
    .attr('y', 0)
    .attr('width', x.bandwidth())
    .attr('height', innerH);

  const showTip = (event, d) => {
    if (!tipEl) return;
    const interestPct = d.interestSharePct.toFixed(0);
    const principalPct = d.principalSharePct.toFixed(0);
    tipEl.innerHTML = `
      <strong>Year ${d.year}</strong>
      <span>Annual EMI total: ${money(d.annualTotal)}</span>
      <span class="emi-split-chart__tip-interest">Interest: ${money(d.interest)} (${interestPct}%)</span>
      <span class="emi-split-chart__tip-principal">Principal: ${money(d.principal)} (${principalPct}%)</span>
      <span>Balance left: ${money(d.closingBalance)}</span>
    `;
    tipEl.style.opacity = '1';

    const plot = tipEl.parentElement;
    if (!plot) return;
    const plotRect = plot.getBoundingClientRect();
    const tipW = tipEl.offsetWidth || 180;
    const tipH = tipEl.offsetHeight || 120;
    let left = event.clientX - plotRect.left + 12;
    let top = event.clientY - plotRect.top - tipH - 8;
    if (left + tipW > plotRect.width - 4) left = plotRect.width - tipW - 4;
    if (left < 4) left = 4;
    if (top < 4) top = event.clientY - plotRect.top + 16;
    tipEl.style.left = `${left}px`;
    tipEl.style.top = `${top}px`;
  };

  hit
    .on('mouseenter', (_event, d) => {
      if (typeof onHoverYear === 'function') onHoverYear(d.year);
    })
    .on('mousemove', (event, d) => showTip(event, d))
    .on('mouseleave', () => {
      if (tipEl) tipEl.style.opacity = '0';
      if (typeof onHoverYear === 'function') onHoverYear(null);
    });

  applyBarHighlight(svgEl, throughYear, null);
}

export default function EmiSplitChart({
  yearRows,
  title = 'Yearly EMI split',
  highlightThroughYear = 1,
}) {
  const plotRef = useRef(null);
  const svgRef = useRef(null);
  const tipRef = useRef(null);
  const uid = useId().replace(/:/g, '');
  const { width, height } = useChartSize(plotRef);
  const [hoveredYear, setHoveredYear] = useState(null);
  const hoverRef = useRef(null);
  const { formatInr } = useCalcAmountDisplayMode();
  const formatMoney = useCallback((value) => formatInr(value), [formatInr]);

  // Keep a stable callback for D3 handlers without rebinding on every hover.
  hoverRef.current = setHoveredYear;

  useEffect(() => {
    if (!svgRef.current) return;
    drawEmiSplitChart(
      svgRef.current,
      tipRef.current,
      Array.isArray(yearRows) ? yearRows : [],
      width,
      height,
      highlightThroughYear,
      (year) => {
        if (hoverRef.current) hoverRef.current(year);
      },
      formatMoney,
    );
    setHoveredYear(null);
    // highlightThroughYear is applied in the effect below — omit here to avoid full redraw on slider drag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearRows, width, height, formatMoney]);

  useEffect(() => {
    applyBarHighlight(svgRef.current, highlightThroughYear, hoveredYear);
  }, [highlightThroughYear, hoveredYear, yearRows, width, height]);

  return (
    <figure className="emi-split-chart" aria-label={title}>
      <figcaption className="emi-split-chart__title">{title}</figcaption>
      <div className="emi-split-chart__legend" aria-hidden="true">
        <span className="emi-split-chart__legend-item emi-split-chart__legend-item--interest">
          <i /> Interest
        </span>
        <span className="emi-split-chart__legend-item emi-split-chart__legend-item--principal">
          <i /> Principal
        </span>
      </div>
      <div className="emi-split-chart__plot" ref={plotRef}>
        <svg
          ref={svgRef}
          className="emi-split-chart__svg"
          role="img"
          aria-labelledby={`emi-split-title-${uid}`}
        >
          <title id={`emi-split-title-${uid}`}>{title}</title>
        </svg>
        <div className="emi-split-chart__tooltip" ref={tipRef} role="tooltip" />
      </div>
      <p className="emi-split-chart__hint">
        Drag the year slider above to highlight years so far, or hover a bar for that year
        alone.
      </p>
    </figure>
  );
}
